import React, { useState, useEffect } from 'react';
import './App.css';
import AssetForm from './components/AssetForm';
import AssetList from './components/AssetList';
import UserInfoForm from './components/UserInfoForm';
import { Asset, ProjectionOptions, SimulationResult, UserInfo, WithdrawalStrategy } from './types';
import { runMonteCarloSimulation } from './utils/calculators';
import { v4 as uuidv4 } from 'uuid';
import { SimulationResultComponent, SimulationResultProps } from './components/SimulationResultComponent';

// LocalStorage keys
const STORAGE_KEYS = {
  ASSETS: 'fire-calculator-assets-changed',
  USER_INFO: 'fire-calculator-user-info-changed',
  PROJECTION_OPTIONS: 'fire-calculator-projection-options-changed'
};

const DEFAULT_USER_INFO: UserInfo = {
  currentAge: 40,
  retirementAge: 65,
  annualExpenses: 50000
};

const DEFAULT_ASSETS: Asset[] = [
  {
    id: uuidv4(),
    name: 'U.S. Stock Market',
    amount: 200000,
    interestRate: 7.0,
    volatility: 15.0,
    annualContribution: 10000
  },
  {
    id: uuidv4(),
    name: 'U.S. Bonds',
    amount: 100000,
    interestRate: 3.0,
    volatility: 5.0,
    annualContribution: 4000,
  },
  {
    id: uuidv4(),
    name: 'Real Estate',
    amount: 150000,
    interestRate: 4.0,
    volatility: 10.0,
    annualContribution: 0
  }
];

const DEFAULT_OPTIONS: ProjectionOptions = {
  yearsToProject: 50,
  withdrawalStrategy: WithdrawalStrategy.GUARDRAILS,
  withdrawalRate: 4,
  simulationRuns: 50
}

function App() {

  // State for assets with localStorage persistence
  const [assets, setAssets] = useState<Asset[]>(() => {
    const savedAssets = localStorage.getItem(STORAGE_KEYS.ASSETS);
    return savedAssets ? JSON.parse(savedAssets) : DEFAULT_ASSETS;
  });
  
  // State for user info with localStorage persistence
  const [userInfo, setUserInfo] = useState<UserInfo>(() => {
    const savedUserInfo = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    return savedUserInfo ? JSON.parse(savedUserInfo) : DEFAULT_USER_INFO;
  });
  
  // State for projection options with localStorage persistence
  const [projectionOptions, setProjectionOptions] = useState<ProjectionOptions>(() => {
    const savedOptions = localStorage.getItem(STORAGE_KEYS.PROJECTION_OPTIONS);
    return savedOptions ? JSON.parse(savedOptions) : DEFAULT_OPTIONS;
  });

  useEffect(() => {
    console.log("Persisting data", {
      userInfo,
      projectionOptions,
      assets
    })
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo))
    localStorage.setItem(STORAGE_KEYS.PROJECTION_OPTIONS, JSON.stringify(projectionOptions))
    localStorage.setItem(STORAGE_KEYS.ASSETS, JSON.stringify(assets))
  }, [userInfo, projectionOptions, assets])
  
  // State for simulation results (not persisted as they can be recalculated)
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null);

    // Run simulation
    const runSimulation = React.useCallback(() => {
      if (assets.length === 0) {
        return;
      }
        
      // Use setTimeout to allow UI to update before CPU-intensive calculation
      setTimeout(() => {
        // Clone assets to avoid modifying the original
        const assetsCopy: Asset[] = JSON.parse(JSON.stringify(assets));
        
        const result = runMonteCarloSimulation(
          assetsCopy,
          userInfo,
          projectionOptions,
          
        );
        
        setSimulationResult(result);
      }, 100);
    }, [assets, projectionOptions, userInfo]);
  
  // Auto-run simulation when the app loads if there are assets
  useEffect(() => {
    if (assets.length > 0 && !simulationResult) {
      runSimulation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Run only on initial render

  useEffect(() => {
    if (assets.length > 0 && simulationResult) {
      console.log("Running simulation")
      runSimulation();
    }
  }, [assets, projectionOptions, userInfo])
  
  // Calculate total asset value
  const totalAssetValue = assets.reduce((sum, asset) => sum + asset.amount, 0);
  
  // Add sample data
  const addSampleData = () => {
    setAssets(DEFAULT_ASSETS);
    setUserInfo(DEFAULT_USER_INFO);
    setProjectionOptions(DEFAULT_OPTIONS);
  };
  
  // Add asset handler
  const handleAddAsset = (asset: Asset) => {
    setAssets([...assets, asset]);
  };
  
  // Remove asset handler
  const handleRemoveAsset = (id: string) => {
    setAssets(assets.filter(asset => asset.id !== id));
  };
  
  // Update asset handler
  const handleUpdateAsset = (updatedAsset: Asset) => {
    setAssets(assets.map(asset => 
      asset.id === updatedAsset.id ? updatedAsset : asset
    ));
  };
  
  // Update user info handler
  const handleUpdateUserInfo = (updatedInfo: UserInfo) => {
    console.log("Setting user info", updatedInfo)
    setUserInfo(updatedInfo);
  };
  
  // Update projection options handler
  const handleUpdateOptions = (updatedOptions: ProjectionOptions) => {
    console.log("Setting options", updatedOptions)
    setProjectionOptions(updatedOptions);
  };

  // Clear all saved data
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all your data? This action cannot be undone.')) {
      localStorage.removeItem(STORAGE_KEYS.ASSETS);
      localStorage.removeItem(STORAGE_KEYS.USER_INFO);
      localStorage.removeItem(STORAGE_KEYS.PROJECTION_OPTIONS);
      
      setAssets(DEFAULT_ASSETS);
      setUserInfo(DEFAULT_USER_INFO);
      setProjectionOptions(DEFAULT_OPTIONS);
      setSimulationResult(null);
    }
  };
  
  // Export data as JSON file
  const exportData = () => {
    const data = {
      assets,
      userInfo,
      projectionOptions
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fire-calculator-data.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Import data from JSON file
  const importData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);
        
        if (data.assets && Array.isArray(data.assets)) {
          setAssets(data.assets);
        }
        
        if (data.userInfo && typeof data.userInfo === 'object') {
          setUserInfo(data.userInfo);
        }
        
        if (data.projectionOptions && typeof data.projectionOptions === 'object') {
          setProjectionOptions(data.projectionOptions);
        }
        
        // Reset file input
        if (event.target) {
          event.target.value = '';
        }
      } catch (error) {
        alert('Error importing data. Please check the file format.');
        console.error('Import error:', error);
      }
    };
    reader.readAsText(file);
  };
  
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-blue-600 text-white p-6">
        <div className="container mx-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold">FIRE & Compound Interest Calculator</h1>
              <p className="text-xl mt-2">Plan your financial independence and retirement</p>
            </div>
            <div className="flex space-x-2">
              <input
                type="file"
                id="import-data"
                accept=".json"
                onChange={importData}
                className="hidden"
              />
              <label
                htmlFor="import-data"
                className="cursor-pointer bg-green-700 hover:bg-green-800 text-white py-2 px-4 rounded-md text-sm transition-colors flex items-center"
              >
                Import Data
              </label>
              <button 
                onClick={exportData}
                className="bg-blue-700 hover:bg-blue-800 text-white py-2 px-4 rounded-md text-sm transition-colors"
              >
                Export Data
              </button>
              <button 
                onClick={clearAllData}
                className="bg-red-700 hover:bg-red-800 text-white py-2 px-4 rounded-md text-sm transition-colors"
              >
                Reset All Data
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto py-8 px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="md:col-span-1">
            <div className="mb-8">
              {assets.length === 0 && (
                <button
                  onClick={addSampleData}
                  className="w-full bg-green-500 text-white py-3 px-4 rounded-md hover:bg-green-600 transition-colors mb-6"
                >
                  Add Sample Data
                </button>
              )}
              
              <AssetForm onAddAsset={(handleAddAsset)} />
            </div>
            
            <UserInfoForm
              userInfo={userInfo}
              onUpdateUserInfo={handleUpdateUserInfo}
              totalAssetValue={totalAssetValue}
            />
          </div>
          
          {/* Middle and Right Columns */}
          <div className="md:col-span-2">
            <AssetList 
              assets={assets} 
              onRemoveAsset={handleRemoveAsset} 
              onUpdateAsset={handleUpdateAsset} 
            />
            
            {/* Simulation Options Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
              <h2 className="text-2xl font-bold mb-4">Simulation Options</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Years to Project
                  </label>
                  <input
                    type="number"
                    value={projectionOptions.yearsToProject}
                    onChange={(e) => handleUpdateOptions({
                      ...projectionOptions,
                      yearsToProject: Math.max(1, parseInt(e.target.value) || 0)
                    })}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Withdrawal Strategy
                  </label>
                  <select
                    value={projectionOptions.withdrawalStrategy}
                    onChange={(e) => handleUpdateOptions({
                      ...projectionOptions,
                      withdrawalStrategy: e.target.value as WithdrawalStrategy
                    })}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={WithdrawalStrategy.GUARDRAILS}>Guardrails</option>
                    <option value={WithdrawalStrategy.CONSTANT_PERCENTAGE}>Constant Percentage</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Withdrawal Rate: {Math.round(projectionOptions.withdrawalRate)}%
                  </label>
                  <div className="flex items-center">
                    <span className="text-xs mr-2">0%</span>
                    <div className="relative flex-grow mx-2">
                      <div className="absolute w-full h-2 bg-gray-200 rounded-lg">
                        <div 
                          className="h-full bg-blue-300 rounded-lg" 
                          style={{
                            width: `${(Math.round(projectionOptions.withdrawalRate) / 50) * 100}%`
                          }}
                        ></div>
                      </div>
                      <input
                        type="range"
                        value={Math.round(projectionOptions.withdrawalRate)}
                        onChange={(e) => handleUpdateOptions({
                          ...projectionOptions,
                          withdrawalRate: parseInt(e.target.value)
                        })}
                        className="w-full h-2 bg-transparent appearance-none cursor-pointer relative z-10"
                        min={0}
                        max={50}
                        step={1}
                      />
                    </div>
                    <span className="text-xs ml-2">50%</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div className="text-xs text-green-500 font-medium">Conservative (3-4%)</div>
                    <div className="text-xs text-orange-500 font-medium text-center">Moderate (4-5%)</div>
                    <div className="text-xs text-red-500 font-medium text-right">Aggressive (5%+)</div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Simulation Runs
                  </label>
                  <input
                    type="number"
                    value={projectionOptions.simulationRuns}
                    onChange={(e) => handleUpdateOptions({
                      ...projectionOptions,
                      simulationRuns: Math.max(1, parseInt(e.target.value) || 0)
                    })}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                    <div>
                      <button 
                        onClick={runSimulation}
                        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-colors"
                      >
                        Recalculate Simulation
                      </button>
                    </div>
                  </div>
              </div>
            </div>

            {/* Simulation Results */}
            <SimulationResultComponent simulationResult={simulationResult} userInfo={userInfo}/>
          </div>
        </div>
      </main>
      
      <footer className="bg-gray-800 text-white p-6">
        <div className="container mx-auto text-center">
          <p>
            FIRE & Compound Interest Calculator - 
            <span className="text-gray-400">
              This calculator is for educational purposes only and should not be considered financial advice.
            </span>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
